import {
   EwusAccountBlockedFault,
   EwusAccountLoginFault,
   EwusPasswordExpiredFault,
   EwusPatient,
   EwusStatusCheckCommand,
   EwusStatusVerificationResult,
} from '@dreryk/egabinet-ewus-frontend-api';
import { Pesel } from '@dreryk/personal-external-identifier-common-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { dispatchSnackbarNegativeEvent } from '@dreryk/react-components';

import { translationKeys } from './translations';
import { useEwusLoginModal } from './ewusLogin/useEwusLoginModal';

type EwusPatientDownloadActionRenderProps = {
   downloadEwusPatient: () => Promise<EwusStatusVerificationResult>;
};

type EwusPatientDownloadActionProps = {
   children: (renderProps: EwusPatientDownloadActionRenderProps) => JSX.Element;
   onSuccess: (ewusPatient: EwusPatient | undefined) => void;
   pesel: Pesel;
};

export const EwusPatientDownloadAction = (props: EwusPatientDownloadActionProps): JSX.Element => {
   const { openEwusLoginModal, renderEwusLoginModal } = useEwusLoginModal();

   const { executeAsync: downloadEwusPatient } = useCqrsCommand<
      EwusStatusVerificationResult,
      void,
      EwusAccountLoginFault | EwusPasswordExpiredFault | EwusAccountBlockedFault
   >(
      () => {
         const command = new EwusStatusCheckCommand();
         command.pesel = props.pesel;
         return command;
      },
      {
         onSuccess: ({ ewusLoginRequired, ewusStatus, patient }) => {
            if (ewusLoginRequired) {
               return openEwusLoginModal();
            }

            if (ewusStatus === 'VERIFICATION_FAILURE') {
               return dispatchSnackbarNegativeEvent(translationKeys.unableToRetrievePatientDataFromEwus);
            }

            return props.onSuccess(patient);
         },

         onFault: {
            'egabinet-ewus-frontend-api:EwusAccountLoginFault': () => {
               dispatchSnackbarNegativeEvent(translationKeys.patientEntitlementStatusMessageMap.ACCOUNT_LOGIN_ERROR);
            },
            'egabinet-ewus-frontend-api:EwusPasswordExpiredFault': () => {
               dispatchSnackbarNegativeEvent(translationKeys.patientEntitlementStatusMessageMap.PASSWORD_EXPIRED);
            },
            'egabinet-ewus-frontend-api:EwusAccountBlockedFault': () => {
               dispatchSnackbarNegativeEvent(translationKeys.patientEntitlementStatusMessageMap.ACCOUNT_BLOCKED);
            },
         },
      }
   );

   return (
      <>
         {props.children({
            downloadEwusPatient: downloadEwusPatient,
         })}

         {renderEwusLoginModal({
            onLogin: downloadEwusPatient,
         })}
      </>
   );
};
